<template>
  <list-layout :tableTools="toggleTableTools" >
    <template slot="header">
      <BaseHeader :content="header" />
    </template>
    <template slot="table-core">
      <base-table-pagination
        :total="pagination.total"
        :currentPage="pagination.currentPage"
        :perPage="pagination.pageSize"
        @changeCurrentPage="changeCurrentPage"
        @changePerPage="changePerPage"
      ></base-table-pagination>
      <div class="flex flex-row w-100">
        <base-table-searches @toggleSearchesDialog="toggleSearchesDialog" />
        <base-table-search @toggleFilters="toggleFilters" />
      </div>
    </template>
    <template slot="table-tags">
      <base-table-filter-tags  :tags="tags" @removeTag="removeFilterTagHanlder" @removeAllTags="removeAllTagsHandler" @saveTags="saveTagsHandler"/>
    </template>
    <template slot="table-component">
      <kendo-grid-component
        :spanish="spanish"
        :dataResult='kendo.dataResult'
        :kendo="kendo"
        :loading='isGeneralLoading'
        :selectedField="selectedField"
        @datastatechange='dataStateChange'
        @columnreorder='columnReorder'
        @rowclick='openPath'
        @columnssubmit='onColumnsSubmit'
        @sortchange='sortChange'
        @filterchange='filterChange'
        @selectionchange="onSelectionChange"
        @headerselectionchange="onHeaderSelectionChange">
      </kendo-grid-component>
    </template>
    <template slot="layout-extend">
      <base-footer>
        <template slot="actions">
          <base-button-responsive v-if="showBackButton && headerUrl" mode="secondary" :text="$t('app.back')" icon="cross" @click="goBack" :loading="isGeneralLoading"/>
          <base-button-responsive mode='primary' :text="labelBtnExcel" icon='excelxls' @click='exportExcel' :loading='isGeneralLoading || loadingExcel' :disabled="loadingExcel"/>
          <base-button-responsive v-for="(action, index) in footerAcciones" :key="index" mode="primary" :text="action.text" :icon="action.icon" @click="action.handler"
            :loading="isGeneralLoading" />
        </template>
      </base-footer>
      <base-table-select-actions-expand
        :baseDropDownVisible="getAccionesPosibles.length > 0"
        :visibility="hasSelected"
        :selected="rowsSelected"
        :total="rowsTotal"
      >
        <template slot="commons">
          <base-button size="xs" mode="tools" @click="unselect">
            <base-icon name="vall" size="sm" class="mr-1" />
            {{$t('app.unselect')}}
          </base-button>
        </template>
        <template slot="more">
          <span v-for="action in getAccionesPosibles" :key="action.IdAccion">
            <base-button-item-list theme="dark" @click="executeAction(action)">
              <base-icon v-if="action.icon" :name="action.icon" class="mr-2" />
                {{ action.Descripcion }}
            </base-button-item-list>
          </span>
        </template>
      </base-table-select-actions-expand>
      <slot name="dialogs" />
      <component
        :is="FilterPanel"
        :panelOpen="filterPanel" @togglePanel="toggleFilters" @applyFilters="applyFiltersHandler" 
        :vuexnamespace="vuexnamespace"
        />
      <create-search-dialog 
        :numberSearches="numberSearches"
        :idVista="idVista" 
        :dialogOpen="saveSearchDialog" 
        :tags="tags" 
        @toggleDialog="toggleDialogSearches" 
        @applyDialog="applySearchHandler" />
      <filter-panel-searches 
        ref="filterPanelSearches" 
        :idVista="idVista" 
        :vuexnamespace="vuexnamespace" 
        :panelOpen="searchesPanel" 
        @togglePanel="toggleSearchesDialog" 
        @applyFilters="applyFiltersHandler" 
        @updateNumberSearches="updateNumberSearches"/>
    </template>
  </list-layout>
</template>

<script>
import { i18n } from '@/i18n'
import { isAssignableTo } from '@/services/functions/isAssignableTo'
import getNestedValue from '@/services/functions/getNestedValue'
import waitUntil from '@/services/functions/waitUntil'
import ListLayout from '@/components/layout/ListLayout.vue'
import FilterPanelSearches from '@/components/searches/SearchesPanel.vue'
import { process } from '@progress/kendo-data-query'
import setNestedValue from '@/services/functions/setNestedValue'
import hasNestedValue from '@/services/functions/hasNestedValue'
import KendoGridComponent from '../../components/KendoGridComponent.vue'
import { Dialog } from 'element-ui'
import CreateSearchDialog from '@/components/searches/CreateSearchDialog.vue'

var cachedEntity = null
class BreadCrumb {
  /** @type {string?} */ url = '';
  /** @type {string?} */ name = '';
  /** @type {string[]?} */ paths;
  /** @type {string} */ separator = ' ';
  /** @type {string?} */ getter = '';
  /** @type {string?} */ setter = '';
  /** @type {boolean} */ forceDynamic = true;
  /** @type {Function} */ routeCallback = (data, breadcrumb) => ({ path: this.url, name: data });
  /** @type {Function} */ dataCallback = (entity, breadcrumb) => (this.name ? this.name : this.paths?.map(path => getNestedValue(path, entity)).join(this.separator)) || '';
}

export default {
  name: 'table-list-view',
  components: {
    ListLayout,
    FilterPanelSearches,
    KendoGridComponent,
    CreateSearchDialog,
    Dialog,
  },
  props: {
    getDetalle: {
      type: Function,
    },
    getPag: {
      type: Function,
      required: true,
    },
    getExcel: {
      type: Function,
      required: true,
    },
    getParams: {
      type: Function,
      required: true,
    },
    getParamsMultiple: {
      type: Function,
      default: data => ({})
    },
    getParamsDetalle: {
      type: Function,
      default: data => ({})
    },
    getParamsPath: {
      type: Function,
      default: data => ({})
    },
    getParamsData: {
      type: Function,
      default: data => ({})
    },
    disableRoutes: {
      type: Function,
    },
    normalizeData: {
      type: Function,
    },
    FilterPanel: null,
    routeName: String,
    excelName: String,
    idVista: Number,
    headerTitle: String,
    headerIcon: String,
    headerUrl: String,
    vuexnamespace: String,
    pagResponsePath: {
      type: String,
      default: ''
    },
    detailResponsePath: {
      type: String,
      default: ''
    },
    payloadSingleId: { 
      type: [String, Array], 
      required: true,
      validator: value => (Array.isArray(value) ? value : [value]).every(x => typeof x === 'string') && value.length
    },
    breadcrumbName: String,
    breadcrumbs: {
      type: Array,
      default: () => [],
      validator: value => value.every(x => isAssignableTo(x, BreadCrumb))
    },
    listaAcciones: {
      type: Array,
      default: () => []
    },
    footerAcciones: {
      type: [],
      default: () => []
    },
    showBackButton: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      // Searches
      numberSearches: 0,
      saveSearchDialog: false,
      searchesPanel: false,
      // All
      loadingExcel: false,
      spanish: true,
      hasClientFilterData: false,
      selectedField: 'selected',
      filterPanel: false,
      showAllRows: true,
      loadingDataTable: false,
      data: null,
      selected: [],
      localBreadcrumbs: [],
      loadingEntity: false
    }
  },
  computed: {
    header: function () {
      return {
        title: this.headerTitle,
        icon: this.headerIcon,
        breadcrumb: this.localBreadcrumbs,
        url: this.headerUrl,
        params: this.$route.params
      }
    },
    isClientFilterActive () {
      return this.$store.getters[this.vuexnamespace + '/isClientFilterActive']
    },
    actualFilterNav () {
      return this.$store.getters['app/idClienteFilterNav']
    },
    labelBtnExcel: function () {
      if (this.hasSelected) return `${i18n.t('app.export_excel')} (${i18n.t('app.selected')})`
      return i18n.t('app.export_excel')
    },
    kendo: function () {
      return this.$store.getters[this.vuexnamespace + '/getKendo']
    },
    isHeadCoreReady: function () {
      return this.$store.getters.isHeadCoreReady
    },
    toggleTableTools: function () {
      return this.selected.length > 0
    },
    rowsSelected: function () {
      return this.selected.length
    },
    rowsTotal: function () {
      return this.selected.length
    },
    setLabelTowShow: function () {
      return this.showAllRows ? i18n.t('app.only_selected') : i18n.t('app.see_all')
    },
    masterFilters: function () {
      return this.$store.getters[this.vuexnamespace + '/getMasterFilters']
    },
    isGeneralLoading: function () {
      return this.$store.getters.isLoading
    },
    headerCoreFilters: function () {
      return this.$store.getters['auth/queryHeaders']
    },
    tags: function () {
      return this.$store.getters[this.vuexnamespace + '/getTags']
    },
    pagination: function () {
      return this.$store.getters[this.vuexnamespace + '/getPaginationInfo']
    },
    areAllSelected () {
      return this.data.length === this.selected.length
    },
    hasSelected () {
      return this.selected.length > 0
    },
    getAccionesPosibles () {
      return this.listaAcciones
    },
  },
  watch: {
    $route (to, from) {
      this.updateOnRouteChange(to, from)
    },
    headerCoreFilters: {
      // immediate: true,
      deep: true,
      handler: 'headerCoreFiltersHandler'
    },
    data (val, oldVal) {
      const dataState = {
        skip: this.kendo.skip,
        take: this.kendo.take,
        sort: this.kendo.sort,
        group: this.kendo.group
      }
      const localGroups = dataState.group || []
      const localData = val || []
      if (localData.length && localGroups.length) {
        localData.forEach(x => {
          localGroups.forEach(group => {
            if (!hasNestedValue(group.field, x, { checkNull: true, checkUndefined: true })) {
              setNestedValue(group.field, x, '')
            }
          })
        })
      }
      this.kendo.dataResult = process(localData, dataState)
      this.unselect()
    },
    breadcrumbs: {
      deep: true,
      handler: function (value) {
        this.updateBreadcrumbs(value, null)
      }
    },
  },
  created: async function () {
    if (this.$route.params.data) {
      this.filterByParamsData()
    }
  },
  mounted: async function () {
    this.$store.commit(this.vuexnamespace + '/setPageSize', this.kendo.take)

    var entity = null
    if (typeof this.getDetalle === 'function' && this.disableRoutes) {
      entity = await this.fetchEntity()
      this.disableRoutes(entity)
    }
    this.updateBreadcrumbs(this.breadcrumbs, entity)
    
    if (this.actualFilterNav && this.isClientFilterActive && !this.hasClientFilterData) {
      if (this.vuexnamespace !== 'distributors') {
        const filter = this.masterFilters
        filter.empresa = { label: i18n.t('clients.entity_name'), value: this.actualFilterNav }
        this.$store.commit(this.vuexnamespace + '/setMasterFilters', filter)
      }
    }

    this.data = await this.applyFilters()
  },
  methods: {
    async waitUntilReady () {
      await waitUntil(() => !this.loadingEntity, 500)
    },
    updateBreadcrumbs: async function (breadcrumbsData = [], entity = null) {
      var needRoute = false
      if (breadcrumbsData?.length && (typeof this.getDetalle === 'function' || (breadcrumbsData.every(x => x?.name) && !breadcrumbsData.at(-1).forceDynamic))) {
        const breadcrumbs = []
        const data = breadcrumbsData.map(x => Object.assign(new BreadCrumb(), x))
        for (const key in data) {
          var routeData = null
          const breadcrumb = data[key]
          if (key === (data.length - 1).toString()) {
            breadcrumb.getter = breadcrumb.getter || 'app/headerBreadCrumb'
            breadcrumb.setter = breadcrumb.setter || 'app/setheaderBreadCrumb'
            breadcrumb.url = ''
            if (breadcrumb.forceDynamic) {
              breadcrumb.paths = breadcrumb.paths || [[breadcrumb.name]]
              breadcrumb.name = ''
            }
          }
          if (this.$store.getters['app/fromRouteName'] !== null && breadcrumb.getter) {
            routeData = this.$store.getters[breadcrumb.getter]
          } else {
            if (!entity && !breadcrumb.name && (breadcrumb.paths?.length || breadcrumb.dataCallback?.name)) {
              entity = await this.fetchEntity()
            }
            routeData = breadcrumb.dataCallback(entity, this, breadcrumb)
            if (breadcrumb.setter) {
              needRoute ||= !!entity
              this.$store.commit(breadcrumb.setter, routeData)
            }
          }

          breadcrumbs.push(breadcrumb.routeCallback(routeData, this, breadcrumb))
        }
        if (needRoute) {
          this.$store.commit('app/setfromRoute', { name: this.$route.name, params: this.$route.params, query: this.$route.query })
        }
        this.localBreadcrumbs = breadcrumbs
      }
    },
    fetchEntity: async function () {
      if (cachedEntity) {
        return cachedEntity
      }
      if (this.loadingEntity) {
        await this.waitUntilReady()
        return cachedEntity
      }
      this.loadingEntity = true
      cachedEntity = await this.downloadData(this.getParamsDetalle())
      this.loadingEntity = false
      return cachedEntity
    },
    filterByParamsData: function () {
      const data = this.$route.params.data
      const filters = {}
      if (data) {
        data.filters.forEach(filter => {
          if (filter.type === 'cliente') {
            this.hasClientFilterData = true
            const cliente = { label: i18n.t('clients.entity_name'), value: { Nombre: filter.data.label, idCliente: filter.data.id } }
            filters.empresa = cliente
          } else {
            filters[filter.type] = filter.data
          }
        })
        this.$store.commit(this.vuexnamespace + '/setMasterFilters', filters)
      }
    },
    updateAllSelectedHeader: function () {
      if (!this.hasSelected) {
        this.kendo.columns[0].headerSelectionValue = false
      } else {
        this.kendo.columns[0].headerSelectionValue = this.areAllSelected
        this.$emit('selectionChange', this.selected)
      }
    },
    goBack: function () {
      if (this.headerUrl) {
        this.$router.push({ name: this.headerUrl, params: this.$route.params })
      }
    },
    openPath: function (event) {
      const breadcrumb = event.dataItem[this.breadcrumbName]
      const params = this.getParamsPath(event.dataItem)
      this.$store.commit('app/setheaderBreadCrumb', breadcrumb)
      this.$router.push({ name: this.routeName, params })
    },
    toggleFilters: function () {
      this.filterPanel = !this.filterPanel
    },
    toggleSearchesDialog: function () {
      this.searchesPanel = !this.searchesPanel
    },
    removeFilterTag: function (tag) {
      this.tags.splice(this.tags.indexOf(tag), 1)
    },
    createAppState: function (dataState) {
      this.kendo.take = dataState.take
      this.kendo.skip = dataState.skip
      if (dataState.group) {
        // eslint-disable-next-line no-return-assign
        // dataState.group.map(group => group.aggregates = this.aggregates)
      }
      this.kendo.group = dataState.group
      this.kendo.filter = dataState.filter
      this.kendo.sort = dataState.sort
    },
    dataStateChange: async function (event) {
      this.createAppState(event.data)
      this.data = await this.applyFilters()
    },
    columnReorder: function (options) {      
      this.sendUpdatedColumns(options.columns)      
      // Fix temporal para mostrar columnas después de reordenar
      this.kendo.columns.forEach((column, index) => {
        if (options.columns.filter(x => x.field === column.field).length === 0) {
          column.orderIndex = index + this.kendo.columns.length
          options.columns.push(column)
        }
      })
      this.kendo.columns = options.columns
    },
    exportExcel: async function () {
      this.$store.commit('app/setLoading', true)
      this.loadingExcel = true
      const data = this.$store.getters[this.vuexnamespace + '/getAppliedFilters']
      const params = { ...this.getParams(data) }

      const objOrder = this.getObjOrderMixin(this.kendo.group, this.kendo.sort)
      try {
        const objColumnas = this.getColumnsGroupExcelMixin(this.kendo.columns, this.kendo.group)
        const payload = { order: objOrder.order, grupos: objColumnas, ...this.getParamsMultiple(data) }
        if (this.hasSelected) {
          var mapper = Array.isArray(this.payloadSingleId) ? x => this.preserve(this.payloadSingleId, x) : x => getNestedValue(this.payloadSingleId, x)
          const selectedIDs = this.selected.map(mapper)
          payload.listaIDs = selectedIDs
        }
        const response = await this.getExcel(payload, params)
        const blob = new Blob([response.data], { type: response.data.type, encoding: 'UTF-8' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        let filename = this.excelName
        filename = decodeURI(filename)
        link.download = filename
        link.click()
      } catch (error) {
        console.error(error)
        if (error.response?.data) {
          const errorText = await error.response.data.text()
          console.error(errorText)
        } else {
          this.doFeedback()
        }
      } finally {
        this.$store.commit('app/setLoading', false)
        this.loadingExcel = false
      }
    },
    onColumnsSubmit: function (columnsState) {
      this.sendUpdatedColumns(columnsState)
      this.kendo.columns = columnsState
    },
    sortChange: function (e) {
      let sort = e.sort
      if (Array.isArray(e)) {
        sort = e
      }
      const dataState = {
        skip: this.kendo.skip,
        take: this.kendo.take,
        sort: sort,
        group: this.kendo.group,
        filter: this.kendo.filter
      }
      this.dataStateChange({ data: dataState })
    },
    filterChange: function (filter) {
      const dataState = {
        skip: this.kendo.skip,
        take: this.kendo.take,
        sort: this.kendo.sort,
        group: this.kendo.group,
        filter: filter
      }
      this.dataStateChange({ data: dataState })
    },
    expandChange: function (event) {
      event.dataItem[event.target.$props.expandField] = !event.dataItem[event.target.$props.expandField]
    },
    updateOnRouteChange: async function (to, from) {
      await this.applyFilters()
    },
    applyFilters: async function (eventData) {
      this.loadingDataTable = true
      const data = this.$store.getters[this.vuexnamespace + '/getAppliedFilters']
      this.$store.commit(this.vuexnamespace + '/setClientFilterActive', !!((data.empresa && data.empresa.value.idCliente === this.actualFilterNav?.idCliente) || (this.hasClientFilterData)))
      const params = { ...this.getParams(data) }
      const objOrder = { ...this.getParamsMultiple(data), ...this.getObjOrderMixin(this.kendo.group, this.kendo.sort) }
      try {
        const res = await this.getItemsData(objOrder, params)
        this.data = typeof this.normalizeData === 'function' ? this.normalizeData(res) : res
      } catch (error) {
        this.$store.commit('setIsLoading', false)
        this.doFeedback()
      }
      this.updateAllSelectedHeader()
      return this.data
    },
    getItemsData: async function (objOrder, filters) {
      this.$store.commit('setAjaxRequestPending', true)
      this.$store.commit('setIsLoading', true)
      const params = {
        paginate: true,
        results: this.pagination.pageSize,
        page: this.pagination.currentPage,
        ...filters
      }
      const res = await this.getMainData(objOrder, params)
      const response = (this.pagResponsePath ? getNestedValue(this.pagResponsePath, res) : (res.Respuesta || res.respuesta)) || []
      this.data = [response].flat()
      const pagination = {
        total: res.meta?.total,
        currentPage: res.meta?.currentPage,
        totalPages: res.meta?.lastPage,
        pageSize: res.meta?.perPage
      }
      this.$store.commit(this.vuexnamespace + '/setPaginationInfo', pagination)
      this.$store.commit('setIsLoading', false)
      this.loadingDataTable = false
      return this.data
    },
    async getMainData (objOrder, filters) {
      const params = {
        paginate: true,
        results: this.pagination.pageSize,
        page: this.pagination.currentPage,
        ...filters
      }
      const data = await this.getPag(objOrder, params)
      if (data.blnError === true) {
        this.doFeedback(`${data.msgError} [${data.codError}]`)
      }
      return data
    },
    applyFiltersHandler: async function (eventData) {
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.data = await this.applyFilters(eventData)
    },
    changeCurrentPage: async function (event) {
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', event)
      this.data = await this.applyFilters(null)
    },
    changePerPage: async function (event) {
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.$store.commit(this.vuexnamespace + '/setPageSize', event)
      this.kendo.take = event
      this.data = await this.applyFilters(null)
    },
    removeFilterTagHanlder: function (tag) {
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      const filter = this.$store.getters[this.vuexnamespace + '/getAppliedFilters']
      delete filter[tag[0]]
      this.$store.commit(this.vuexnamespace + '/setMasterFilters', filter)
      this.applyFiltersHandler(null)
    },
    headerCoreFiltersHandler: function (obs) {
      if (this.isHeadCoreReady) {
        this.applyFilters(null)
      }
    },
    removeAllTagsHandler: function () {
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.$store.commit(this.vuexnamespace + '/resetMasterFilters')
      this.applyFiltersHandler(null)
    },
    tableSortByChangeHandler: function ({ prop, order }) {
      this.$store.commit(this.vuexnamespace + '/setSort', prop)
      this.$store.commit(this.vuexnamespace + '/setSortOrder', order)
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.applyFilters(null)
    },
    downloadData: async function (params) {
      try {
        this.$store.commit('setIsLoading', true)
        const data = await this.getDetalle(params)
        if (data.blnError === true) {
          this.doFeedback(`${data.msgError} [${data.codError}]`)
        } else {
          return (this.detailResponsePath ? getNestedValue(this.detailResponsePath, data) : (data.Respuesta || data.respuesta)) || null
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
      return null
    },
    unselect: function () {
      const checked = false
      this.mapSelectionKendoGrouping(checked)
      this.updateAllSelectedHeader()
    },
    onHeaderSelectionChange: function (event) {
      const checked = event.event.target.checked
      this.mapSelectionKendoGrouping(checked)
      this.updateAllSelectedHeader()
    },
    executeAction: async function (action) {
      this.$emit('executeAction', action, this.selected)
    },
    onSelectionChange: function (event) {
      event.dataItem[this.selectedField] = !event.dataItem[this.selectedField]
      this.selected = event.dataItems.filter(d => d.selected === true)
      this.updateAllSelectedHeader()
    },
    saveTagsHandler: function () {
      this.saveSearchDialog = !this.saveSearchDialog
    },
    toggleDialogSearches: function () {
      this.saveSearchDialog = !this.saveSearchDialog
    },
    applySearchHandler: async function () {
      this.toggleDialogSearches()
      // Manda actualizar lista de búsquedas
      this.$refs.filterPanelSearches.updateData()
    },
    updateNumberSearches: async function (number) {
      this.numberSearches = number
    },
  },
  beforeDestroy () {
    cachedEntity = null
  },
}
</script>
